import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';

type Role = 'owner' | 'admin' | 'member' | 'viewer' | null;

interface WorkspaceContextType {
    workspaceId: string | null;
    currentRole: Role;
    memberCount: number;
    memberLimit: number;
    isLoading: boolean;
    permissions: {
        canEdit: boolean;           // Create/edit OKR, boards, tasks
        canInvite: boolean;         // Invite new members
        canManageRoles: boolean;    // Change member roles
        canRemoveMembers: boolean;  // Remove members (not self)
        canDeleteWorkspace: boolean;// Delete the workspace
        canManageSettings: boolean; // Change workspace settings
    };
    refetch: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
    const workspace = useAuthStore((s) => s.workspace);
    const user = useAuthStore((s) => s.user);
    const [currentRole, setCurrentRole] = useState<Role>(null);

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['workspace-members', workspace?.id],
        queryFn: () => api.getWorkspaceMembers(workspace!.id),
        enabled: !!workspace?.id && !!user?.id,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    useEffect(() => {
        if (data?.members && user?.id) {
            const member = data.members.find(m => m.user_id === user.id);
            setCurrentRole(member?.role as Role || null);
        }
    }, [data, user?.id]);

    const permissions = {
        // Viewers cannot edit anything
        canEdit: currentRole !== 'viewer' && currentRole !== null,
        // Only owner and admin can invite
        canInvite: currentRole === 'owner' || currentRole === 'admin',
        // Only owner can manage roles
        canManageRoles: currentRole === 'owner',
        // Owner and admin can remove members (with restrictions)
        canRemoveMembers: currentRole === 'owner' || currentRole === 'admin',
        // Only owner can delete workspace
        canDeleteWorkspace: currentRole === 'owner',
        // Owner and admin can manage settings
        canManageSettings: currentRole === 'owner' || currentRole === 'admin',
    };

    return (
        <WorkspaceContext.Provider
            value={{
                workspaceId: workspace?.id || null,
                currentRole,
                memberCount: data?.members?.length || 1,
                memberLimit: data?.memberLimit || 5,
                isLoading,
                permissions,
                refetch,
            }}
        >
            {children}
        </WorkspaceContext.Provider>
    );
}

export function useWorkspace() {
    const context = useContext(WorkspaceContext);
    if (!context) {
        throw new Error('useWorkspace must be used within a WorkspaceProvider');
    }
    return context;
}

// Convenience hook for just permissions
export function usePermissions() {
    const { permissions, currentRole, isLoading } = useWorkspace();
    return { ...permissions, currentRole, isLoading };
}
